import { BonePose, clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';
import { WARD_GROW } from '../../models/oracle.ts';

/*
 * El Oráculo de tinta (models/oracle.ts). Idle and walk loop (it floats: the body bobs, the root stays on the pool);
 * attack lashes the lower right claw across the foe (starts cocked, the blow lands at ~0.13 s, when the simulation
 * deals it); hit; stun (loop); die (the ink drains out of the robe and it slumps down into its own puddle); and its
 * power: `ward` lifts the orb in all four hands and swells the bubble out round it (the `ward` bone grows
 * ×WARD_GROW, and stays up at the end), `crack` shudders the bubble and splits it into two halves that fall away.
 *
 * Conventions as in human.ts: a negative X swings an arm forward and up; +X on a trunk leans it forward (on the
 * hanging robe, it sweeps the hem back); the figure faces +Z and its `R` limbs are on +X (+Z on them raises them
 * out to the side, -Z on the `L` ones). The robe and the hanging forearms trail their chains (see `trail`).
 */

type B =
  | 'root' | 'body' | 'robe' | 'chest' | 'head' | 'halo' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'arm2L' | 'fore2L' | 'arm2R' | 'fore2R'
  | 'drip' | 'drip2' | 'orb' | 'ward' | 'wardA' | 'wardB';
type P = KeyPose<B>;
type Pose = (b: Bones<B>, t: number) => void;

/** The bubble at full size (scale of the `ward` bone). */
const FULL = WARD_GROW;

/** A pose sheet that makes any bone it is asked for (for sampling a pose at another time). */
const scratch = (): Bones<B> => {
  const o: Record<string, BonePose> = {};
  return new Proxy(o, { get: (_, k: string) => (o[k] ??= new BonePose()) }) as Bones<B>;
};

/** A bone that trails its chain: `hang` cancels that much of the chain's tilt (1 hangs plumb), `k` swings it against the motion. */
interface Trail {
  bone: B;
  chain: readonly B[];
  lag: number;
  k: number;
  hang: number;
}

const TRAILS: readonly Trail[] = [
  { bone: 'robe', chain: ['root', 'body'], lag: 0.12, k: 0.9, hang: 0.45 },
  { bone: 'fore2L', chain: ['root', 'body', 'chest', 'arm2L'], lag: 0.08, k: 0.5, hang: 0.2 },
  { bone: 'fore2R', chain: ['root', 'body', 'chest', 'arm2R'], lag: 0.08, k: 0.5, hang: 0.2 },
];

/**
 * Secondary motion: samples the pose a beat (and two) earlier and swings each trailing bone against how its chain
 * moved in between, overshooting once. Deterministic: it only re-reads the pose itself.
 */
const trail = (pose: Pose, dur: number, loop: boolean): Pose => (b, t) => {
  pose(b, t);
  const wrap = (x: number): number => (loop ? ((x % dur) + dur) % dur : Math.max(0, x));
  const at = (x: number): Bones<B> => {
    const s = scratch();
    pose(s, wrap(x));
    return s;
  };
  for (const f of TRAILS) {
    const s1 = at(t - f.lag), s2 = at(t - 2 * f.lag);
    const sum = (bb: Bones<B>, ax: 'rx' | 'rz'): number => f.chain.reduce((a, n) => a + bb[n][ax], 0);
    for (const ax of ['rx', 'rz'] as const) {
      const c0 = sum(b, ax), c1 = sum(s1, ax), c2 = sum(s2, ax);
      b[f.bone][ax] += -f.hang * c0 - f.k * (c0 - c1) + 0.55 * f.k * (c1 - c2);
    }
  }
};

const oclip = (name: string, dur: number, loop: boolean, pose: Pose, squash?: (t: number) => [number, number, number], label?: string): Clip<B> =>
  clip<B>(name, dur, loop, trail(pose, dur, loop), squash, label);

/** Stooped a little over the orb, the head bowed to it. */
const ST: P = { chest: [0.05, 0, 0], head: [0.1, 0, 0] };

/** A drop falling from the hem to the pool over [a, b] of a loop phase, shrinking into it as it lands (hidden while it re-forms). */
const fall = (bp: BonePose, p: number, a: number, b: number): void => {
  if (p >= a && p <= b) {
    const k = (p - a) / (b - a);
    bp.pos(0, -0.13 * k * k, 0).scale(k > 0.88 ? Math.max(0.05, 1 - (k - 0.88) * 8) : 1);
    return;
  }
  // Between falls a new drop gathers on the hem.
  const u = ((p - b + 1) % 1) / (1 - (b - a));
  bp.scale(0.05 + 0.95 * Math.min(1, u * 1.3));
};

const idle = (): Clip<B> =>
  oclip('idle', 4, true, (b, t) => {
    const p = phase(t, 4);
    const w = p * TAU;
    apply(b, ST);
    // It hangs in the air on a slow tide, the robe swaying under it, the orb rising and sinking against it.
    b.body.pos(0, 0.035 * Math.sin(w * 2), 0).rot(0.02 * Math.sin(w * 2 + 0.6), 0.05 * Math.sin(w), 0.015 * Math.sin(w + 1));
    b.chest.rot(0.025 * Math.sin(w * 2 - 0.4), 0.04 * Math.sin(w + 0.5));
    b.head.rot(0.04 * Math.sin(w - 0.5), -0.12 * Math.sin(w + 0.2), 0.06 * Math.sin(w * 2 - 1));
    b.orb.pos(0, 0.012 * Math.sin(w * 2 + 1.4), 0).rot(0, w, 0);
    // The halo turns once a cycle; the lower claws flex, slowly, out of step.
    b.halo.rot(0, w, 0);
    b.arm2L.rot(0.05 * Math.sin(w + 0.3), 0, -0.04 * Math.sin(w * 2));
    b.arm2R.rot(0.05 * Math.sin(w + 2.1), 0, 0.04 * Math.sin(w * 2 + 1));
    b.fore2L.rot(-0.15 * Math.max(0, Math.sin(w * 2 + 0.5)) ** 2);
    b.fore2R.rot(-0.15 * Math.max(0, Math.sin(w * 2 + 2.2)) ** 2);
    b.foreL.rot(0.03 * Math.sin(w * 2 + 1.4));
    b.foreR.rot(0.03 * Math.sin(w * 2 + 1.4));
    b.armL.rot(0.03 * Math.sin(w * 2 + 1.4));
    b.armR.rot(0.03 * Math.sin(w * 2 + 1.4));
    // Ink drips off the hem into the pool.
    fall(b.drip, p, 0.1, 0.35);
    fall(b.drip2, p, 0.55, 0.8);
  });

/** A gliding drift: leaning into the way, the robe and the lower claws streaming back, no step at all. */
const walk = (dur = 1.4): Clip<B> =>
  oclip('walk', dur, true, (b, t) => {
    const w = phase(t, dur) * TAU;
    const sn = Math.sin(w);
    apply(b, ST);
    b.body.pos(0, 0.03 * Math.sin(2 * w), 0).rot(0.14 + 0.02 * Math.cos(2 * w), 0.05 * sn, 0.04 * sn);
    b.robe.rot(0.12 + 0.04 * Math.sin(2 * w - 0.8), 0.05 * sn, 0.06 * Math.sin(w - 0.6));
    b.chest.rot(-0.04 + 0.02 * Math.cos(2 * w - 0.6), -0.05 * sn, -0.03 * sn);
    b.head.rot(-0.08, -0.04 * sn, -0.03 * sn);
    b.arm2L.rot(0.45 + 0.06 * Math.sin(w * 2), 0, -0.12);
    b.arm2R.rot(0.45 + 0.06 * Math.sin(w * 2 + 1), 0, 0.12);
    b.fore2L.rot(0.25);
    b.fore2R.rot(0.25);
    b.orb.pos(0, 0.01 * Math.sin(2 * w + 1), 0).rot(0, w, 0);
    b.halo.rot(0, w, 0);
    fall(b.drip, phase(t, dur), 0.2, 0.7);
  });

/** Cocked: the lower right claw drawn up and back, the trunk wound away. Then lashed across (lands at 0.13 s). */
const COCK: P = {
  body: [-0.06, -0.15, 0], chest: [-0.1, -0.35, 0.05], head: [-0.05, 0.25, 0],
  arm2R: [0.35, 0, 0.55], fore2R: [2.0, 0, 0.2], arm2L: [-0.2, 0, -0.3], fore2L: [-0.3, 0, 0],
  orb: [0, 0, 0, -0.05, 0.03, -0.04], root: [0, 0, 0, 0, 0.02, -0.03],
};
const LASH: P = {
  body: [0.14, 0.2, 0], chest: [0.3, 0.42, -0.05], head: [0.2, -0.15, 0], robe: [0.12, 0, 0],
  arm2R: [-0.85, 0.3, -0.55], fore2R: [-0.95, 0, -0.1], arm2L: [0.25, 0, -0.15], fore2L: [-0.1, 0, 0],
  orb: [0, 0, 0, 0.04, -0.02, 0.1, 1.25], root: [0, 0, 0, 0, -0.02, 0.1],
};

const attack = (): Clip<B> =>
  oclip('attack', 0.7, false, (b, t) => {
    timeline(b, t, [
      [0, COCK],
      [0.13, LASH, inCubic],
      [0.28, { ...LASH, arm2R: [-0.75, 0.3, -0.5], fore2R: [-0.8, 0, -0.1], orb: [0, 0, 0, 0.03, -0.02, 0.08, 1.1] }, outBack],
      [0.7, ST, inOut],
    ], { head: 0.02, fore2R: 0.025 });
    if (t > 0.13) {
      const k = settle(t - 0.13, 5, 11);
      b.arm2R.rot(-0.08 * k);
      b.chest.rot(0.04 * k);
    }
    b.halo.rot(0, 2 * t, 0);
  }, (t) => {
    const k = t > 0.13 ? Math.max(0, settle(t - 0.13, 3, 10)) : 0;
    return [1 + 0.03 * k, 1 - 0.04 * k, 1 + 0.03 * k];
  });

const hit = (): Clip<B> =>
  oclip('hit', 0.4, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    b.body.rot(-0.14 * k, 0.06 * k).pos(0, 0.03 * k, -0.07 * k);
    b.chest.rot(-0.22 * k, 0.1 * k, 0.05 * k);
    b.head.rot(-0.35 * k, -0.15 * k, 0.1 * k);
    b.arm2L.rot(-0.3 * k, 0, -0.25 * k);
    b.arm2R.rot(-0.3 * k, 0, 0.25 * k);
    b.halo.rot(0.2 * k, 0.3 * k, -0.15 * k);
    b.orb.pos(0, 0, -0.03 * k).scale(1 - 0.2 * Math.abs(k));
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.05 * k, 1 - 0.06 * k, 1 + 0.05 * k];
  });

/** Dazed: it drifts low and lists round, the orb sagging, the claws hanging limp, the halo wobbling off true. */
const stun = (): Clip<B> =>
  oclip('stun', 1.6, true, (b, t) => {
    const w = phase(t, 1.6) * TAU;
    apply(b, ST, 0.5);
    b.body.pos(0, -0.06 + 0.02 * Math.sin(w * 2), 0).rot(0.05 * Math.sin(w), 0, 0.1 * Math.cos(w));
    b.chest.rot(0.3 + 0.05 * Math.sin(w * 2), 0, -0.08 * Math.cos(w));
    b.head.rot(0.35 + 0.15 * Math.sin(w + 0.8), 0.3 * Math.cos(w + 0.8), 0.25 * Math.sin(w + 0.8));
    b.halo.rot(0.3 * Math.sin(w), w, 0.3 * Math.cos(w));
    b.armL.rot(0.5, 0, 0.1);
    b.armR.rot(0.5, 0, -0.1);
    b.foreL.rot(0.3);
    b.foreR.rot(0.3);
    b.orb.pos(0, -0.22, -0.05).rot(0.3 * Math.sin(w), w, 0);
    b.arm2L.rot(0.25 + 0.1 * Math.sin(w + 1.2), 0, 0.25);
    b.arm2R.rot(0.25 + 0.1 * Math.sin(w + 1.6), 0, -0.25);
    b.fore2L.rot(0.3);
    b.fore2R.rot(0.3);
  });

/** It shrieks with all four arms up; then the ink drains out of its robe and it slumps down into its own puddle. */
const die = (): Clip<B> =>
  oclip('die', 1.8, false, (b, t) => {
    const heap: P = {
      body: [0.25, 0, 0.1, 0, -0.78, 0.04, 0.6], robe: [0.3, 0, 0, 0, 0, 0, 0.25], chest: [1.0, 0.2, 0.1], head: [0.5, 0.4, 0.2],
      armL: [0.6, 0, -0.9], armR: [0.4, 0, 1.0], arm2L: [0.2, 0, -1.2], arm2R: [0.3, 0, 1.1], fore2L: [0.4, 0, 0], fore2R: [0.4, 0, 0],
      orb: [0, 0, 0, 0.05, -0.62, 0.05, 0.75], halo: [0.4, 0, 0, 0, -0.3, 0, 0.05],
    };
    const shriek: P = {
      body: [-0.1, 0, 0, 0, 0.08, 0], chest: [-0.4, 0, 0], head: [-0.55, 0, 0],
      armL: [-1.4, 0, -0.5], armR: [-1.4, 0, 0.5], arm2L: [-0.6, 0, -1.1], arm2R: [-0.6, 0, 1.1], fore2L: [-0.4, 0, 0], fore2R: [-0.4, 0, 0],
      orb: [0, 0, 0, 0, 0.3, -0.1, 1.2], halo: [0, 0, 0, 0, 0.05, 0, 1.3],
    };
    timeline(b, t, [
      [0, ST],
      [0.22, shriek, outExpo],
      [0.5, { ...shriek, chest: [-0.35, 0, 0], head: [-0.5, 0.1, 0] }],
      [1.0, { body: [0.1, 0, 0.05, 0, -0.4, 0.02, 0.8], robe: [0.15, 0, 0, 0, 0, 0, 0.55], chest: [0.5, 0.1, 0.05], head: [0.4, 0.2, 0.1], armL: [0.2, 0, -0.4], armR: [0.2, 0, 0.4], arm2L: [0, 0, -0.7], arm2R: [0, 0, 0.7], orb: [0, 0, 0, 0, -0.25, 0, 0.9], halo: [0.2, 0, 0, 0, -0.15, 0, 0.6] }, inQuad],
      [1.45, heap, inQuad],
      [1.8, { ...heap, chest: [1.05, 0.2, 0.1] }, outBack],
    ], { head: 0.04, arm2L: 0.03, arm2R: 0.03, halo: 0.06 });
    const tr = envelope(t, 0.2, 0.28, 0.5, 0.6) * Math.sin(t * 70);
    b.chest.rot(0, 0.03 * tr, 0.02 * tr);
    b.head.rot(0.05 * tr);
    b.halo.rot(0, 3 * t, 0);
  }, (t) => {
    // The drain: it flattens out into the puddle (ends on the ground).
    const k = span(t, 0.6, 1.6, inQuad);
    const b = t > 1.45 ? Math.max(0, settle(t - 1.45, 3, 9)) : 0;
    return [1 + 0.3 * k + 0.05 * b, 1 - 0.55 * k - 0.06 * b, 1 + 0.3 * k + 0.05 * b];
  });

/** The ward: all four hands lift the orb high and the bubble of ink-glass swells out round it (and stays up). */
const ward = (): Clip<B> =>
  oclip('ward', 1.2, false, (b, t) => {
    const up: P = {
      body: [-0.06, 0, 0, 0, 0.08, 0], chest: [-0.25, 0, 0], head: [-0.4, 0, 0],
      armL: [-1.55, 0, 0.12], foreL: [-0.2, 0, 0], armR: [-1.55, 0, -0.12], foreR: [-0.2, 0, 0],
      arm2L: [-0.5, 0, -1.25], arm2R: [-0.5, 0, 1.25], fore2L: [-0.5, 0, 0], fore2R: [-0.5, 0, 0],
      orb: [0, 0, 0, 0, 0.6, -0.1, 1.3],
    };
    timeline(b, t, [
      [0, ST],
      [0.22, { ...ST, chest: [0.25, 0, 0], head: [0.25, 0, 0], body: [0, 0, 0, 0, -0.04, 0], arm2L: [0.2, 0, 0.2], arm2R: [0.2, 0, -0.2], orb: [0, 0, 0, 0, -0.06, -0.04, 0.85] }, outQuad],
      [0.5, up, outBack],
      [0.9, { ...up, chest: [-0.22, 0, 0], head: [-0.36, 0, 0], orb: [0, 0, 0, 0, 0.57, -0.1, 1.2] }],
      [1.2, { ...up, chest: [-0.2, 0, 0], head: [-0.34, 0, 0], orb: [0, 0, 0, 0, 0.56, -0.1, 1.2] }, inOut],
    ], { head: 0.03, fore2L: 0.04, fore2R: 0.04 });
    // The bubble swells out of the robe past full, then settles with a shiver that dies away.
    const k = span(t, 0.3, 0.7, outBack);
    const sh = envelope(t, 0.6, 0.7, 0.9, 1.2) * 0.02 * Math.sin(t * 50);
    b.ward.rot(0, 0.6 * t, 0).scale(1 + (FULL - 1) * (k + sh));
    b.halo.rot(0, 4 * t, 0).scale(1 + 0.3 * envelope(t, 0.3, 0.55, 0.8, 1.2));
    const tr = envelope(t, 0.45, 0.55, 0.85, 1.0) * Math.sin(t * 60);
    b.armL.rot(0.02 * tr);
    b.armR.rot(-0.02 * tr);
  }, (t) => {
    const k = envelope(t, 0.25, 0.45, 0.8, 1.2, outCubic);
    return [1 - 0.015 * k, 1 + 0.03 * k, 1 - 0.015 * k];
  }, 'Burbuja');

/** The bubble cracks: it shudders, the seam splits, the two halves fall open and break away; the oracle flinches. */
const crack = (): Clip<B> =>
  oclip('crack', 0.5, false, (b, t) => {
    apply(b, ST);
    const sh = envelope(t, 0, 0.03, 0.16, 0.22) * Math.sin(t * 95);
    const open = span(t, 0.18, 0.5, inQuad);
    const gone = span(t, 0.3, 0.5, inCubic);
    b.ward.rot(0.015 * sh, 0, 0.02 * sh).scale(1 + (FULL - 1) * (1 + 0.025 * sh + 0.06 * span(t, 0.12, 0.2, outQuad)) * (1 - gone));
    b.wardA.rot(-0.2 * open, 0, 1.25 * open).pos(-0.006 * open, 0, 0);
    b.wardB.rot(0.2 * open, 0, -1.25 * open).pos(0.006 * open, 0, 0);
    // The oracle flinches as its ward breaks, the orb dimming down.
    const k = settle(t - 0.16, 3, 7) * span(t, 0.12, 0.2, outExpo);
    b.body.rot(-0.1 * k).pos(0, 0, -0.04 * k);
    b.chest.rot(-0.15 * k, 0.06 * k);
    b.head.rot(-0.25 * k, 0, 0.08 * k);
    b.arm2L.rot(-0.2 * k, 0, -0.2 * k);
    b.arm2R.rot(-0.2 * k, 0, 0.2 * k);
    b.orb.scale(1 - 0.25 * Math.abs(k));
    b.halo.rot(0.15 * sh, 3 * t, 0.1 * sh);
  }, (t) => {
    const k = Math.max(0, settle(t - 0.16, 3, 9)) * span(t, 0.12, 0.2);
    return [1 + 0.03 * k, 1 - 0.04 * k, 1 + 0.03 * k];
  }, 'Se raja');

/** Its clips (see models/oracle.ts for the bones). */
export const oracleClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), ward(), crack()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { oracle: oracleClips };
